import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  User,
  Calendar,
  Clock,
  Target,
  MessageSquare,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Star,
  Trash2,
  Volume2,
  Play,
  Pause,
  Pin,
  PinOff
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Slider } from "@/components/ui/slider";

export default function TeamMemberSession() {
  const { memberId, sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Replay state
  const [hasRecording, setHasRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const [isPinned, setIsPinned] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  useEffect(() => {
    fetchSessionDetail();
    // fetchSessionDetail is stable; re-run when the member/session in the URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, sessionId]);

  // Check recording and pin status
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API}/recordings/exists/${sessionId}`, { credentials: "include" });
        if (res.ok) { const d = await res.json(); setHasRecording(d.exists); }
      } catch (e) { console.warn("Failed to check recording for member session:", e); }
    };
    check();
  }, [sessionId]);

  useEffect(() => {
    if (data?.session?.pinned) setIsPinned(true);
  }, [data]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        if (audioRef.current.src?.startsWith("blob:")) URL.revokeObjectURL(audioRef.current.src);
      }
    };
  }, []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/sessions/${sessionId}`, {
        method: "DELETE", credentials: "include"
      });
      if (res.ok) {
        toast.success("Session deleted");
        navigate("/team");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to delete session");
      }
    } catch {
      toast.error("Failed to delete session");
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
      return;
    }

    if (audioLoading) return;
    setAudioLoading(true);

    fetch(`${API}/recordings/stream/${sessionId}`, { credentials: "include" })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio();
        audio.src = url;
        audio.ontimeupdate = () => {
          setCurrentTime(audio.currentTime);
          setIsPlaying(!audio.paused);
        };
        audio.onended = () => { setIsPlaying(false); setCurrentTime(0); };
        audio.onpause = () => setIsPlaying(false);
        audio.onplay = () => setIsPlaying(true);
        audio.onloadedmetadata = () => {
          if (!Number.isFinite(audio.duration)) {
            audio.currentTime = 1e7;
            audio.addEventListener("timeupdate", function seekHandler() {
              audio.removeEventListener("timeupdate", seekHandler);
              setDuration(audio.duration);
              audio.currentTime = 0;
              audioRef.current = audio;
              setAudioLoading(false);
              audio.play();
            });
          } else {
            setDuration(audio.duration);
            audioRef.current = audio;
            setAudioLoading(false);
            audio.play();
          }
        };
      })
      .catch(() => { toast.error("Failed to load recording"); setAudioLoading(false); });
  };

  const handleSeek = (value) => {
    const time = value[0];
    if (audioRef.current && Number.isFinite(time) && Number.isFinite(duration) && duration > 0) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handlePin = async () => {
    setPinLoading(true);
    try {
      const endpoint = isPinned ? "unpin" : "pin";
      const res = await fetch(`${API}/data-management/${endpoint}/${sessionId}`, {
        method: "POST", credentials: "include"
      });
      if (res.ok) {
        setIsPinned(!isPinned);
        toast.success(isPinned ? "Session unpinned" : "Session saved");
      }
    } catch { toast.error("Failed to update pin"); }
    finally { setPinLoading(false); }
  };

  const fetchSessionDetail = async () => {
    try {
      const response = await fetch(`${API}/team/member/${memberId}/session/${sessionId}`, {
        credentials: "include"
      });
      if (response.ok) {
        setData(await response.json());
      } else if (response.status === 403) {
        toast.error("Access denied");
        navigate("/team");
      } else {
        toast.error("Session not found");
        navigate("/team");
      }
    } catch (error) {
      toast.error("Failed to load session");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="team-member-session-page">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { member, session } = data;
  const feedback = session.feedback || {};
  const metrics = feedback.metrics || {};

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="team-member-session-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/team")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarImage src={member.picture} alt={member.name} />
              <AvatarFallback>{member.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-heading font-bold">{session.scenario_name}</h1>
              <p className="text-muted-foreground">{member.name}'s Session</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={isPinned ? "default" : "outline"}
            size="sm"
            onClick={handlePin}
            disabled={pinLoading}
            data-testid="pin-session-btn"
          >
            {isPinned ? <PinOff className="w-4 h-4 mr-1" /> : <Pin className="w-4 h-4 mr-1" />}
            {isPinned ? "Saved" : "Save"}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            data-testid="delete-session-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
        {session.score !== null && session.score !== undefined && (
          <div className="text-center">
            <div className={`text-4xl font-bold ${
              session.score >= 80 ? "text-green-500" :
              session.score >= 60 ? "text-yellow-500" : "text-red-500"
            }`}>
              {session.score}%
            </div>
            <p className="text-xs text-muted-foreground">Overall Score</p>
          </div>
        )}
      </div>

      {/* Session Info */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Date</p>
              <p className="font-medium">
                {session.started_at ? new Date(session.started_at).toLocaleDateString() : "N/A"}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <Badge variant={session.status === "completed" ? "default" : "secondary"}>
                {session.status}
              </Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Messages</p>
              <p className="font-medium">{session.messages?.length || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 flex items-center gap-3">
            <Target className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Difficulty</p>
              <p className="font-medium capitalize">{session.difficulty || "Medium"}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Metrics */}
        {Object.keys(metrics).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(metrics).map(([key, value]) => (
                <div key={key}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm capitalize">{key.replace(/_/g, " ")}</span>
                    <span className="text-sm font-medium">{value}%</span>
                  </div>
                  <Progress value={value} className="h-2" />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Feedback */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5" />
              AI Feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {feedback.summary && (
              <div>
                <h4 className="font-medium mb-2">Summary</h4>
                <p className="text-sm text-muted-foreground">{feedback.summary}</p>
              </div>
            )}
            
            {feedback.strengths && feedback.strengths.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Strengths
                </h4>
                <ul className="space-y-1">
                  {feedback.strengths.map((strength, i) => (
                    <li key={`strength-${i}-${strength.slice(0,20)}`} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {feedback.areas_for_improvement && feedback.areas_for_improvement.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                  Areas for Improvement
                </h4>
                <ul className="space-y-1">
                  {feedback.areas_for_improvement.map((area, i) => (
                    <li key={`area-${i}-${area.slice(0,20)}`} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-yellow-500 mt-1">•</span>
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!feedback.summary && !feedback.strengths && !feedback.areas_for_improvement && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No feedback available for this session
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Specific Feedback */}
      {feedback.specific_feedback?.length > 0 && (
        <Card data-testid="specific-feedback-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Specific Feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {feedback.specific_feedback.map((item, i) => (
              <div key={`sf-${i}-${(item.quote || "").slice(0,20)}`} className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm italic text-muted-foreground mb-2">
                  "{item.quote}"
                </p>
                <p className="text-sm font-medium">{item.feedback}</p>
                {item.suggestion && (
                  <p className="text-sm text-primary mt-2">Suggestion: {item.suggestion}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Recommended Next Steps */}
      {feedback.next_steps?.length > 0 && (
        <Card data-testid="next-steps-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Recommended Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {feedback.next_steps.map((step, i) => (
                <li key={`step-${i}-${step.slice(0,20)}`} className="flex items-start gap-3 text-sm">
                  <span className="font-bold text-primary mt-0.5">{i + 1}.</span>
                  {step}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Session Replay */}
      {hasRecording && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-primary" />
              Session Replay
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Button
                variant="outline" size="icon"
                className="w-12 h-12 rounded-full flex-shrink-0"
                onClick={handlePlayPause}
                disabled={audioLoading}
                data-testid="replay-play-btn"
              >
                {audioLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </Button>
              <div className="flex-1 space-y-1">
                <Slider
                  value={[currentTime]}
                  max={Number.isFinite(duration) && duration > 0 ? duration : 100}
                  step={0.1}
                  onValueChange={handleSeek}
                  className="cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transcript */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Conversation Transcript
          </CardTitle>
          <CardDescription>Full conversation between {member.name} and the AI client</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <div className="space-y-4 pr-4">
              {session.messages && session.messages.length > 0 ? (
                session.messages.map((msg, i) => (
                  <div key={`msg-${i}-${msg.role}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === "user" 
                        ? "bg-primary text-primary-foreground rounded-br-sm" 
                        : "bg-muted rounded-bl-sm"
                    }`}>
                      <p className="text-xs font-medium mb-1 opacity-70">
                        {msg.role === "user" ? member.name : "AI Client"}
                      </p>
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No transcript available for this session
                </p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this roleplay session, its transcript, feedback, and any recording. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
